import { FiAward, FiGift } from "react-icons/fi";
import { getRewardfulReferral } from "../../utils/rewardful";
import { useState, useEffect } from "react";
import { axiosInstance } from "../../config";
import TrialPlanPicker from "../HardPaywall/TrialPlanPicker";
import "./UpgradeModal.css";

// Apply a % discount to a "$24" style string → "$9.60" (trims trailing .00/.x0).
function discountPrice(priceStr, pct) {
  const n = Number(String(priceStr).replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(n) || !pct) return priceStr;
  const d = n * (1 - pct / 100);
  const s = d.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
  return `$${s}`;
}

// Opus-mirrored pricing: 1 credit = 1 minute of source video processed.
const PLANS = [
  {
    key: "Starter",
    label: "Starter",
    tagline: "For solo clippers and streamers getting started.",
    monthlyPrice: "$15",
    annualPrice: null, // monthly only, as on OpusClip
    credits: 150,
    videoNote: "150 min of video",
    highlight: false,
    features: [
      "9:16 + 16:9 aspect ratios",
      "HD rendered clips",
      "AI animated captions",
      "Watermark-free exports",
      "5 streamers",
      "Music recommendations for clips",
    ],
  },
  {
    key: "Pro",
    label: "Pro",
    tagline: "For full-time clippers and serious streamers.",
    monthlyPrice: "$29",
    annualPrice: "$14.50",
    credits: 300,
    videoNote: "300 min of video",
    highlight: true,
    badge: "Most Popular",
    features: [
      "9:16 + 16:9 aspect ratios",
      "HD rendered clips",
      "AI animated captions",
      "Watermark-free exports",
      "5 streamers",
      "Music recommendations for clips",
      "Hook-detection tuned for livestreams",
      "Priority render queue",
    ],
  },
  {
    key: "Premium",
    label: "Premium",
    tagline: "For agencies, networks, and serious clipping operations.",
    monthlyPrice: "$145",
    annualPrice: "$72.50",
    credits: 1500,
    videoNote: "25 hours of video",
    highlight: false,
    badge: "Most Credits",
    features: [
      "9:16 + 16:9 aspect ratios",
      "HD rendered clips",
      "AI animated captions",
      "Watermark-free exports",
      "5 streamers",
      "Music recommendations for clips",
      "Most credits for heavy clippers",
      "Multi-creator workspace",
      "Dedicated support",
    ],
  },
];

const CREDIT_PACKS = [
  { id: "pack_50",   label: "50 credits",    price: "$5",  priceNote: null,         desc: "+50 min of video processing" },
  { id: "pack_150",  label: "150 credits",   price: "$14", priceNote: "Save 7%",    desc: "+150 min · Starter-equivalent" },
  { id: "pack_300",  label: "300 credits",   price: "$27", priceNote: "Save 10%",   desc: "+300 min · Pro-equivalent" },
  { id: "pack_1500", label: "1,500 credits", price: "$90", priceNote: "Best Value", desc: "+25 hours · Premium-equivalent" },
];

// Phase 1 of pricing transition: UI shows new prices but Stripe products
// still bill at old rates. Until Phase 2 (REACT_APP_NEW_PRICING_LIVE=true +
// new Stripe products created), route signup clicks to contact email.
const NEW_PRICING_LIVE = process.env.REACT_APP_NEW_PRICING_LIVE === "true";

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ff7a18" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const UpgradeModal = ({ isOpen, onClose, initialTab = "plans", message, currentPlan, currentInterval = null, ownOffer = false }) => {
  const [loading, setLoading] = useState(null); // planKey or packId
  const [interval, setBillingInterval] = useState(currentInterval || "monthly");
  // Open the toggle on the user's actual billing interval so a current
  // subscriber sees their real plan marked (not the wrong period).
  useEffect(() => {
    if (isOpen && currentInterval) setBillingInterval(currentInterval);
  }, [isOpen, currentInterval]);
  const [tab, setTab] = useState(initialTab); // "plans" | "credits"
  // The modal stays mounted — open on whichever tab the caller asked for.
  useEffect(() => {
    if (isOpen) setTab(initialTab);
  }, [isOpen, initialTab]);
  const [selectedPlanKey, setSelectedPlanKey] = useState(
    PLANS.find((p) => p.highlight)?.key || PLANS[0].key,
  );
  // Regional (PPP) pricing for the visitor — used to show localized discounts.
  const [region, setRegion] = useState(null);
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    axiosInstance
      .get("/billing/region-pricing")
      .then((res) => {
        if (!cancelled) setRegion(res.data || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Founder LTD ($200 one-time) availability + purchase.
  const [foundersSpots, setFoundersSpots] = useState(null);
  const [foundersLoading, setFoundersLoading] = useState(false);
  const [foundersErr, setFoundersErr] = useState("");
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    axiosInstance.get("/founders-spots")
      .then((r) => { if (!cancelled) setFoundersSpots(r.data || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen]);

  // Mid-$1-trial accounts get the trial plan picker (TrialPlanPicker) as the
  // first view: Starter / Pro / Premium, monthly or annual, so they choose how
  // many credits they need instead of only "Start Pro now".
  const [trial, setTrial] = useState(null);
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    axiosInstance.get("/trial/status")
      .then((res) => {
        if (cancelled || !res.data?.trialing) return;
        setTrial(res.data);
        setTab("trial");
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen]);

  const handleFounder = async () => {
    setFoundersErr("");
    setFoundersLoading(true);
    try {
      const res = await axiosInstance.post("/billing/founders/checkout", {});
      if (res.data?.url) { window.location.href = res.data.url; return; }
      setFoundersErr("Couldn't start checkout. Please try again.");
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === "sold_out") setFoundersErr("Founder spots are sold out.");
      else if (code === "already_founder") setFoundersErr("You already have the lifetime deal.");
      else setFoundersErr(err.response?.data?.message || "Couldn't start checkout.");
    } finally {
      setFoundersLoading(false);
    }
  };

  if (!isOpen) return null;
  if (tab === "trial" && trial) {
    return <TrialPlanPicker trial={trial} onClose={onClose} onBuyCredits={() => setTab("credits")} />;
  }

  const regionPct = region?.discountPct || 0;
  const isAnnual = interval === "annual";
  // OWN offer: only the Pro plan is shown, pre-selected, with the 150-credit bonus.
  const visiblePlans = ownOffer ? PLANS.filter((p) => p.key === "Pro") : PLANS;
  const selectedPlan =
    (ownOffer
      ? PLANS.find((p) => p.key === "Pro")
      : PLANS.find((p) => p.key === selectedPlanKey)) || PLANS[0];
  const priceNum = (s) => Number(String(s).replace(/[^0-9.]/g, "")) || 0;
  // Savings on the toggle: the selected plan's, or Pro's when it has no yearly.
  const savingsPlan = selectedPlan.annualPrice ? selectedPlan : PLANS.find((p) => p.key === "Pro");
  const annualSavingsPct = Math.round(
    (1 - priceNum(savingsPlan.annualPrice) / priceNum(savingsPlan.monthlyPrice)) *
      100,
  );

  const handleSubscribe = async (planKey) => {
    if (!NEW_PRICING_LIVE) {
      window.open(
        "mailto:clipbaitla@gmail.com?subject=" +
          encodeURIComponent(`Early access — ${planKey} plan`),
        "_blank"
      );
      return;
    }
    setLoading(planKey);
    try {
      const referral = getRewardfulReferral();
      const res = await axiosInstance.post("/billing/plan/checkout", {
        planName: planKey,
        interval,
        ...(referral ? { referral } : {}),
      });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      console.error("Checkout error:", err);
      setLoading(null);
    }
  };

  const handleBuyCredits = async (packId) => {
    setLoading(packId);
    try {
      const res = await axiosInstance.post("/billing/credits/checkout", { packId });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      console.error("Credits checkout error:", err);
      setLoading(null);
    }
  };

  return (
    <div className="upgrade-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal-v3" onClick={(e) => e.stopPropagation()}>
        <button className="upgrade-modal-close" onClick={onClose}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {tab === "plans" ? (
          <div className="um3-body">
            {/* LEFT: brand + selected-plan benefits */}
            <div className="um3-left">
              <div className="um3-brand">CLIPBAIT</div>
              <h2 className="um3-headline">
                {message || "Upgrade to keep generating."}
              </h2>
              {regionPct > 0 && (
                <div className="um3-region-banner">
                  {regionPct}% regional discount applied
                </div>
              )}
              <div className="um3-benefits-title">
                {selectedPlan.label} {isAnnual ? "Annual" : "Monthly"} Benefits
              </div>
              <div className="um3-credits">
                {selectedPlan.credits.toLocaleString()} credits / month
              </div>
              <div className="um3-video-note">≈ {selectedPlan.videoNote}</div>
              {ownOffer && (
                <div className="um3-own-bonus">
                  <FiGift /> +150 bonus credits on signup. They never expire.
                </div>
              )}
              <ul className="um3-benefits">
                {selectedPlan.features.map((f) => (
                  <li key={f}>
                    <CheckIcon />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* RIGHT: billing toggle, selectable plan rows, CTA */}
            <div className="um3-right">
              <div className="um3-toggle">
                <button
                  className={`um3-toggle-btn${!isAnnual ? " active" : ""}`}
                  onClick={() => setBillingInterval("monthly")}
                >
                  Monthly
                </button>
                <button
                  className={`um3-toggle-btn${isAnnual ? " active" : ""}`}
                  onClick={() => {
                    setBillingInterval("annual");
                    // Starter is monthly only: yearly moves the pick to Pro.
                    if (!PLANS.find((p) => p.key === selectedPlanKey)?.annualPrice) setSelectedPlanKey("Pro");
                  }}
                >
                  Yearly
                  {annualSavingsPct > 0 && (
                    <span className="um3-toggle-save">Save {annualSavingsPct}%</span>
                  )}
                </button>
              </div>

              <div className="um3-plans">
                {visiblePlans.map((plan) => {
                  const isSel = plan.key === selectedPlanKey;
                  // Only "current" when the tier AND the viewed interval match
                  // the user's subscription — so a monthly Pro user can still
                  // switch to annual (and vice-versa) instead of being locked.
                  const isCurrent =
                    currentPlan && currentPlan !== "none" && plan.key === currentPlan &&
                    (!currentInterval || interval === currentInterval);
                  if (isAnnual && !plan.annualPrice) {
                    return (
                      <div key={plan.key} className="um3-plan unavailable" aria-disabled="true">
                        <span className="um3-plan-name">{plan.label}</span>
                        <span className="um3-plan-price"><span className="um3-plan-per">Monthly only</span></span>
                      </div>
                    );
                  }
                  return (
                    <button
                      key={plan.key}
                      className={`um3-plan${isSel ? " selected" : ""}${plan.highlight ? " popular" : ""}${isCurrent ? " current" : ""}`}
                      onClick={() => setSelectedPlanKey(plan.key)}
                    >
                      {isCurrent ? (
                        <span className="um3-plan-badge um3-plan-badge-current">
                          Current plan
                        </span>
                      ) : (
                        plan.badge && (
                          <span className="um3-plan-badge">{plan.badge}</span>
                        )
                      )}
                      <span className="um3-plan-name">
                        {isSel && <span className="um3-plan-check"><CheckIcon /></span>}
                        {plan.label}
                      </span>
                      <span className="um3-plan-price">
                        {regionPct > 0 && (
                          <span className="um3-plan-price-was">
                            {isAnnual ? plan.annualPrice : plan.monthlyPrice}
                          </span>
                        )}
                        {discountPrice(
                          isAnnual ? plan.annualPrice : plan.monthlyPrice,
                          regionPct,
                        )}
                        <span className="um3-plan-per">/month</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {(() => {
                const selectedIsCurrent =
                  currentPlan &&
                  currentPlan !== "none" &&
                  selectedPlan.key === currentPlan &&
                  (!currentInterval || interval === currentInterval);
                return (
                  <button
                    className="um3-cta"
                    onClick={() => handleSubscribe(selectedPlan.key)}
                    disabled={loading === selectedPlan.key || selectedIsCurrent}
                  >
                    {loading === selectedPlan.key ? (
                      <span className="upgrade-modal-spinner" />
                    ) : selectedIsCurrent ? (
                      "Current plan"
                    ) : NEW_PRICING_LIVE ? (
                      `Upgrade to ${selectedPlan.label}`
                    ) : (
                      "Request early access"
                    )}
                  </button>
                );
              })()}

              {!ownOffer && (
                <button className="um3-credits-link" onClick={() => setTab("credits")}>
                  Or buy one-time credits instead
                </button>
              )}

              {/* Founder LTD — one-time $200, lifetime Pro + 300 credits/mo forever */}
              {!ownOffer && (foundersSpots && foundersSpots.soldOut ? (
                <div style={{ marginTop: 12, textAlign: "center", color: "rgba(255,255,255,0.45)", fontSize: 13 }}>
                  <FiAward /> Founder Lifetime — sold out
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleFounder}
                  disabled={foundersLoading}
                  style={{
                    marginTop: 12, width: "100%", textAlign: "left", cursor: foundersLoading ? "default" : "pointer",
                    background: "linear-gradient(135deg, rgba(255,106,0,0.14), rgba(255,138,51,0.05))",
                    border: "1px solid rgba(255,106,0,0.55)", borderRadius: 12, padding: "12px 14px", color: "#fff",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontWeight: 700, color: "#ff8a33" }}><FiAward /> Founder · Lifetime</span>
                    <span style={{ fontWeight: 700 }}>{foundersLoading ? "…" : "$200 once"}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 4 }}>
                    Lifetime Pro + 300 credits every month, forever. Never pay again.
                    {foundersSpots && Number.isFinite(foundersSpots.remaining)
                      ? ` · ${foundersSpots.remaining} of ${foundersSpots.total} left`
                      : ""}
                  </div>
                </button>
              ))}
              {foundersErr && (
                <div style={{ marginTop: 8, color: "#ff6b6b", fontSize: 12, textAlign: "center" }}>{foundersErr}</div>
              )}
            </div>
          </div>
        ) : (
          <div className="upgrade-modal-v2-credits">
            <button className="um3-back-link" onClick={() => setTab(trial ? "trial" : "plans")}>
              {trial ? "← Back" : "← Back to plans"}
            </button>
            <p className="upgrade-modal-v2-credits-note">
              Credits never expire and are used after your monthly plan allowance runs out.
            </p>
            <div className="upgrade-modal-v2-credit-packs">
              {CREDIT_PACKS.map((pack) => (
                <div key={pack.id} className="upgrade-modal-v2-credit-pack">
                  <div className="upgrade-modal-v2-credit-pack-left">
                    <div className="upgrade-modal-v2-credit-label">{pack.label}</div>
                    <div className="upgrade-modal-v2-credit-desc">{pack.desc}</div>
                  </div>
                  <div className="upgrade-modal-v2-credit-pack-right">
                    <div className="upgrade-modal-v2-credit-price-row">
                      <span className="upgrade-modal-v2-credit-price">{pack.price}</span>
                      {pack.priceNote && (
                        <span className="upgrade-modal-v2-credit-save">{pack.priceNote}</span>
                      )}
                    </div>
                    <button
                      className="upgrade-modal-v2-credit-btn"
                      onClick={() => handleBuyCredits(pack.id)}
                      disabled={loading === pack.id}
                    >
                      {loading === pack.id ? <span className="upgrade-modal-spinner" /> : "Buy"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpgradeModal;
